import { c, t } from '../../theme';

/**
 * The standard page heading: an optional section eyebrow, the title, an
 * optional one-line description, and optional right-aligned actions.
 *
 * Every page had been rolling its own — titles at 22px and 24px, bottom
 * margins of 16, 18 and 20px, one page carrying a stray -0.3px of tracking,
 * and `s.title` meaning the heading on fourteen pages but the *subtitle* on
 * CityPricing. This is that pattern, once.
 *
 *   <PageHeader
 *     eyebrow="Finance"
 *     title="Expense Analysis"
 *     subtitle="Workday expense report lines."
 *     actions={<button>New contract</button>}
 *   />
 */
export default function PageHeader({
  eyebrow, title, subtitle, meta, actions, alignActions = 'start',
}) {
  return (
    <div className={`ph-root${alignActions === 'end' ? ' ph-root-end' : ''}`}>
      <style>{css}</style>
      <div className="ph-text">
        {eyebrow && <div className="ph-eyebrow">{eyebrow}</div>}
        <h1 className="ph-title">{title}</h1>
        {subtitle && <p className="ph-sub">{subtitle}</p>}
        {meta && <div className="ph-meta">{meta}</div>}
      </div>
      {actions && <div className="ph-actions">{actions}</div>}
    </div>
  );
}

const css = `
.ph-root {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.ph-text { min-width: 0; }
.ph-eyebrow {
  font-size: ${t.label.fontSize};
  font-weight: ${t.label.fontWeight};
  text-transform: ${t.label.textTransform};
  letter-spacing: ${t.label.letterSpacing};
  color: ${c.textFaint};
  margin-bottom: 5px;
}
.ph-title {
  font-size: ${t.h1.fontSize};
  line-height: ${t.h1.lineHeight};
  font-weight: ${t.h1.fontWeight};
  letter-spacing: ${t.h1.letterSpacing};
  color: ${c.text};
  text-wrap: balance;
}
/* Descriptions are read once to orient, so keep them to a comfortable
   measure rather than letting them run the full width of a wide screen. */
.ph-sub {
  font-size: ${t.bodySm.fontSize};
  line-height: 1.55;
  color: ${c.textMuted};
  margin-top: 6px;
  max-width: 78ch;
}
/* Status chips and provenance notes that belong to the title rather than
   being a description of the page. */
.ph-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 7px;
}
.ph-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
/* Controls that carry their own label sit better aligned to the bottom of
   the title block than to the top of it. */
.ph-root-end { align-items: flex-end; }
.ph-root-end .ph-actions { align-items: flex-end; }

@media (max-width: 640px) {
  .ph-root { margin-bottom: 16px; }
  .ph-actions { width: 100%; }
}
`;
